import React, { useState, useRef, useEffect, useContext } from "react";
import styled from "styled-components";
import ReactPlayer from "react-player";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ShareIcon from '@material-ui/icons/Share';
import { DashboardContext } from "../../dashboard-context";

const VideoWrapper = styled.div`
  display: flex;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  height: auto;
  max-height: 740px;
  max-width: 1400px;
  width: 85%;
  z-index: 350;
  @media (max-width: 900px) {
    width: 97%;
  }
`;

const Video = styled.div`
  background: #000;
  position: relative;
  padding-top: 56.25%;
  flex: 1;
`;

const LinkButton = styled.button`
  background: #FFF;
  border: none;
  border-radius: 100px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 32px;
  top: -48px;
  z-index: 2;
  padding: 4px 12px;
  svg {
    font-size: 1.25rem;
    margin-left: 4px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  position: absolute;
  right: -64px;
  top: -64px;
  z-index: 2;
  svg {
    font-size: 4rem;
  }

  @media (max-width: 900px) {
    right: 0px;
  }
`;

const VideoPopupOverlay = styled.div`
  background: rgba(0, 0, 0, 0.7);
  height: 100%;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 300;
`;

const TimestampContainer = styled.div`
  background-color: ${({ theme }) => theme.color.dark};
  padding: 10px;
  position: relative;
  width: 260px;
  overflow-y: auto;
  span {
    color: #fff;
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 10px;
    padding-left: 8px;
  }
`;

const Divider = styled.div`
  background-color: #fff;
  opacity: 0.4;
  height: 1px;
  width: 100%;
  margin: 2px 0px 4px 0px;
`;

const Timestamp = styled.div`
  background-color: ${({ active, theme }) =>
    active ? theme.color.highlight : theme.color.dark};
  cursor: pointer;
  color: #fff;
  font-size: 0.9rem;
  width: 100%;
  padding: 8px 10px;
  margin: 2px 0px;
  border-radius: 5px;
  &:hover {
    background-color: ${({ theme }) => theme.color.highlight};
  }
`;

const ScrollText = styled.p`
  background: #fff;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  padding: 4px 8px;
  position: sticky;
  bottom: 10px;
  left: 50%;
  transform: translateX(-30%);
  text-align: center;
  width: 142px;
`;

function VideoPlayer({video, open, onClose}) {
  const { openSnackbar } = useContext(DashboardContext);
  const [currentTime, setCurrentTime] = useState(0);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const player = useRef(null);
  const timestampContainerRef = useRef(null);

  useEffect(() => {
    let interval;
    if (open && player.current) {
      interval = setInterval(() => {
        if (player.current) {
          setCurrentTime(player.current.getCurrentTime());
        }
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [open]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 1;
    setIsScrolledToBottom(isBottom);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(video.videoUrl);
    openSnackbar("Link copied to clipboard");
  };

  return (
    <>
      {open && video && (
        <>
          <VideoWrapper>
            <LinkButton onClick={handleCopyLink}>
              Share video
              <ShareIcon />
            </LinkButton>
            <CloseButton onClick={onClose}>
              <CloseIcon />
            </CloseButton>
            <Video>
              <ReactPlayer
                controls
                ref={player}
                url={video.videoUrl}
                config={{
                  file: {
                    attributes: {
                      controlsList: "nodownload",
                    },
                  },
                }}
                playing
                width="100%"
                height="100%"
                style={{ position: "absolute", top: "0", left: "0" }}
              />
            </Video>
            {video?.timestamps?.length > 0 && (
              <TimestampContainer
                ref={timestampContainerRef}
                onScroll={handleScroll}
              >
                <span>Chapters</span>
                <Divider />
                {video.timestamps.map((timestamp, index) => {
                  const isLastPassedTimestamp =
                    currentTime >= timestamp.time &&
                    (index === video.timestamps.length - 1 ||
                      currentTime < video.timestamps[index + 1].time);
                  return (
                    <Timestamp
                      active={isLastPassedTimestamp}
                      onClick={() => player.current.seekTo(timestamp.time)}
                    >
                      {timestamp.title}
                    </Timestamp>
                  );
                })}
                {video?.timestamps?.length > 20 && !isScrolledToBottom && (
                  <ScrollText>
                    Scroll for more <ExpandMoreIcon />
                  </ScrollText>
                )}
              </TimestampContainer>
            )}
          </VideoWrapper>
          <VideoPopupOverlay onClick={onClose} />
        </>
      )}
    </>
  );
}

export default VideoPlayer;
